// i18next initialization. Imported once from main / App so the singleton
// instance is configured before any component calls `useTranslation`.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  DEFAULT_NAMESPACE,
  FALLBACK_LOCALE,
  LOCALE_STORAGE_KEY,
  NAMESPACES,
  SUPPORTED_LOCALES,
  normalizeLocale,
} from './config';

// Eagerly import every English namespace so the app never blocks on a network
// fetch for translations. French mirrors are imported alongside; both are
// small enough to ship in the main bundle for v1 (~tens of KB once filled).
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enJob from './locales/en/job.json';
import enCandidates from './locales/en/candidates.json';
import enInterview from './locales/en/interview.json';
import enCvUpload from './locales/en/cv_upload.json';
import enSettings from './locales/en/settings.json';
import enMarketing from './locales/en/marketing.json';
import enAgents from './locales/en/agents.json';
import enLegal from './locales/en/legal.json';
import enTalentPool from './locales/en/talentPool.json';
import enChatbot from './locales/en/chatbot.json';

import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frDashboard from './locales/fr/dashboard.json';
import frJob from './locales/fr/job.json';
import frCandidates from './locales/fr/candidates.json';
import frInterview from './locales/fr/interview.json';
import frCvUpload from './locales/fr/cv_upload.json';
import frSettings from './locales/fr/settings.json';
import frMarketing from './locales/fr/marketing.json';
import frAgents from './locales/fr/agents.json';
import frLegal from './locales/fr/legal.json';
import frTalentPool from './locales/fr/talentPool.json';
import frChatbot from './locales/fr/chatbot.json';

export const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    job: enJob,
    candidates: enCandidates,
    interview: enInterview,
    cv_upload: enCvUpload,
    settings: enSettings,
    marketing: enMarketing,
    agents: enAgents,
    legal: enLegal,
    talentPool: enTalentPool,
    chatbot: enChatbot,
  },
  fr: {
    common: frCommon,
    auth: frAuth,
    dashboard: frDashboard,
    job: frJob,
    candidates: frCandidates,
    interview: frInterview,
    cv_upload: frCvUpload,
    settings: frSettings,
    marketing: frMarketing,
    agents: frAgents,
    legal: frLegal,
    talentPool: frTalentPool,
    chatbot: frChatbot,
  },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: {
      // React already escapes string output, so disabling i18next's escape
      // avoids double-encoding entities like apostrophes in French copy.
      escapeValue: false,
    },
    react: {
      // Resources are bundled eagerly so namespaces are always synchronously
      // available. Setting useSuspense: false prevents any accidental Suspense
      // throw if a namespace is ever referenced before init completes (e.g.
      // during HMR), which would crash components not wrapped in a boundary.
      useSuspense: false,
    },
    detection: {
      // Cookie + localStorage are authoritative for anonymous flows. Browser
      // `navigator` only seeds the very first visit; after that, the globe
      // owns the value.
      order: ['localStorage', 'cookie', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      lookupCookie: LOCALE_STORAGE_KEY,
      caches: ['localStorage', 'cookie'],
      cookieMinutes: 60 * 24 * 365,
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    returnNull: false,
  });

// Normalize any detected value (e.g. `fr-FR`) to our two-letter set.
const detected = normalizeLocale(i18n.language);
if (detected !== i18n.language) {
  void i18n.changeLanguage(detected);
}

export default i18n;
